/**
 * ImpersonationBanner — iter641 + iter1021
 *
 * Sticky banner когато admin/doctor е влязъл в чужд акаунт.
 * iter1021 — добавен countdown timer за оставащото време до изтичане
 * на impersonation JWT (auto-exit при 0).
 */
import React, { useEffect, useMemo, useState } from "react";
import { ShieldAlert, LogOut, Clock } from "lucide-react";

function readActive() {
  try { return localStorage.getItem("impersonation_active") === "true"; }
  catch { return false; }
}

/** Decode JWT payload without verifying signature (only for reading exp). */
function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    return JSON.parse(atob(b64));
  } catch { return null; }
}

function fmtMMSS(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function doExit(returnPath) {
  try {
    const adminToken = localStorage.getItem("impersonation_admin_token");
    const adminRefresh = localStorage.getItem("impersonation_admin_refresh");
    localStorage.removeItem("impersonation_active");
    localStorage.removeItem("impersonation_target_email");
    localStorage.removeItem("impersonation_admin_token");
    localStorage.removeItem("impersonation_admin_refresh");
    localStorage.removeItem("impersonation_readonly");
    localStorage.removeItem("impersonation_return_path");
    if (adminToken) {
      localStorage.setItem("access_token", adminToken);
      sessionStorage.setItem("nutramilo_access_token", adminToken);
    } else {
      localStorage.removeItem("access_token");
      sessionStorage.removeItem("nutramilo_access_token");
    }
    if (adminRefresh) {
      sessionStorage.setItem("nutramilo_refresh_token", adminRefresh);
    }
  } catch { /* ignore */ }
  window.location.href = returnPath || "/admin";
}

export default function ImpersonationBanner() {
  const [active, setActive] = useState(readActive);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const onStorage = () => setActive(readActive());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // 1s tick for countdown; only while active to save cycles.
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [active]);

  const targetEmail = active ? (localStorage.getItem("impersonation_target_email") || "потребител") : "";
  const readonly = active ? localStorage.getItem("impersonation_readonly") === "true" : false;
  const returnPath = active ? (localStorage.getItem("impersonation_return_path") || "/admin") : "/admin";

  // Compute exp from the current access token.
  const exp = useMemo(() => {
    if (!active) return 0;
    const tok = sessionStorage.getItem("nutramilo_access_token") || localStorage.getItem("access_token") || "";
    const p = decodeJwtPayload(tok);
    return Number(p?.exp || 0);
  }, [active]);

  const remaining = exp > 0 ? exp - now : 0;
  const expired = active && exp > 0 && remaining <= 0;

  // Auto-exit when the impersonation token expires so the UI can't keep
  // showing target-user data with an invalidated Bearer.
  useEffect(() => {
    if (expired) doExit(returnPath);
  }, [expired, returnPath]);

  if (!active) return null;

  const bgCls = readonly ? "bg-sky-500 text-sky-950" : "bg-amber-500 text-amber-950";
  const btnCls = readonly ? "bg-sky-950 text-sky-50 hover:bg-sky-800" : "bg-amber-950 text-amber-50 hover:bg-amber-800";
  // Timer chip color reflects urgency.
  const timerChipCls = remaining <= 60
    ? "bg-red-600 text-red-50 animate-pulse"
    : remaining <= 300
    ? "bg-red-700/90 text-red-50"
    : (readonly ? "bg-sky-950/20 text-sky-950" : "bg-amber-950/15 text-amber-950");

  return (
    <div
      data-testid="impersonation-banner"
      className={`fixed top-0 left-0 right-0 z-[10000] shadow-md ${bgCls}`}
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">
            <strong>{readonly ? "Преглед само за четене:" : "Режим Влез като:"}</strong>{" "}
            <span className="font-mono break-all">{targetEmail}</span>
            {readonly && (
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-sky-950/10 rounded px-1.5 py-0.5">
                Read-only
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {exp > 0 && (
            <span
              data-testid="impersonation-timer"
              title={remaining <= 60 ? "Изтича скоро — приключете работата и излезте" : "Оставащо време на сесията"}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono font-bold ${timerChipCls}`}
            >
              <Clock className="w-3 h-3" />
              {fmtMMSS(remaining)}
            </span>
          )}
          <button
            onClick={() => doExit(returnPath)}
            data-testid="impersonation-exit-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold ${btnCls}`}
          >
            <LogOut className="w-3.5 h-3.5" />
            Излез от режима
          </button>
        </div>
      </div>
    </div>
  );
}
